import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// import Navbar from './Navbar';
import Footer from './Footer';
import {
  Receipt as InvoiceIcon,
  Description as DocumentIcon,
  Assessment as ReportIcon,
  Security as SecurityIcon, 
  Devices as ResponsiveIcon,
  Speed as FastIcon
} from '@mui/icons-material';

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Hide the main navbar and footer for the landing page
  const hideNavbarAndFooter = false;

  const features = [
    {
      icon: <InvoiceIcon color="primary" sx={{ fontSize: 50 }} />,
      title: 'Easy Invoicing',
      description: 'Create professional invoices in minutes with our intuitive interface.'
    },
    {
      icon: <DocumentIcon color="primary" sx={{ fontSize: 50 }} />,
      title: 'Document Management',
      description: 'Keep all your important documents organized in one place.'
    },
    {
      icon: <ReportIcon color="primary" sx={{ fontSize: 50 }} />,
      title: 'Detailed Reports',
      description: 'Generate comprehensive reports to track your business growth.'
    },
    {
      icon: <SecurityIcon color="primary" sx={{ fontSize: 50 }} />,
      title: 'Secure & Private',
      description: 'Your data is encrypted and protected with enterprise-grade security.'
    },
    {
      icon: <ResponsiveIcon color="primary" sx={{ fontSize: 50 }} />,
      title: 'Fully Responsive',
      description: 'Access your account from any device, anywhere, anytime.'
    },
    {
      icon: <FastIcon color="primary" sx={{ fontSize: 50 }} />,
      title: 'Lightning Fast',
      description: 'Built for speed to save you time and improve productivity.'
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* <Navbar hideNavbar={hideNavbarAndFooter} /> */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: 12,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              mb: 3,
              fontSize: isMobile ? '2rem' : '3rem',
              lineHeight: 1.2
            }}
          >
            Welcome to Invoquot
          </Typography>
          <Typography 
            variant="h5" 
            component="p" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto', 
              mb: 6,
              opacity: 0.9
            }}
          >
            The all-in-one solution for managing your invoices, quotations, and business operations with ease.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signin')}
            sx={{
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              backgroundColor: 'white',
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4]
              },
              transition: 'all 0.3s ease'
            }}
          >
            Get Started
          </Button>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          textAlign="center" 
          mb={8}
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            fontSize: isMobile ? '1.8rem' : '2.2rem'
          }}
        >
          Why Choose Invoquot?
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: theme.shadows[6]
                  }
                }}
              >
                <Box sx={{ 
                  width: 80, 
                  height: 80, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 2,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.light + '20'
                }}>
                  {feature.icon}
                </Box>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          py: 10,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              mb: 3,
              fontSize: isMobile ? '1.8rem' : '2.5rem'
            }}
          >
            Ready to Transform Your Business?
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto', 
              mb: 6,
              fontSize: isMobile ? '1rem' : '1.25rem'
            }}
          >
            Join thousands of businesses that trust Invoquot for their invoicing and document management needs.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signin')}
            sx={{
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4]
              },
              transition: 'all 0.3s ease'
            }}
          >
            Get Started for Free
          </Button>
        </Container>
      </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default LandingPage;
